import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Linkedin, Facebook, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useContactModal } from '../context/ContactModalContext';
import { t } from '../data/translations';

const Footer = () => {
  const { language } = useLanguage();
  const { openContactModal } = useContactModal();

  return (
    <footer className="bg-[#131132] text-white pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Logo & Contact */}
          <div className="lg:col-span-2">
            <a href="/" className="inline-block mb-2">
              <span className="text-3xl font-bold">
                <span className="font-light">Work</span>
                <span className="text-[#E8A238]">Pro</span>
              </span>
            </a>
            <p className="text-white/40 text-sm mb-6">part of ALS Group</p>
            <p className="text-white/50 mb-8 max-w-sm text-lg leading-relaxed">
              {t('footer.description', language)}
            </p>
            <div className="space-y-4">
              <a href="tel:+421949303240" className="flex items-center gap-4 text-white/70 hover:text-[#E8A238] transition-colors text-lg">
                <Phone className="w-5 h-5" />
                +421 949 303 240
              </a>
              <a href="mailto:info@alsgroup.sk" className="flex items-center gap-4 text-white/70 hover:text-[#E8A238] transition-colors text-lg">
                <Mail className="w-5 h-5" />
                info@alsgroup.sk
              </a>
              <a href="mailto:cooperation@alsgroup.sk" className="flex items-center gap-4 text-white/70 hover:text-[#E8A238] transition-colors text-lg">
                <Mail className="w-5 h-5" />
                cooperation@alsgroup.sk
              </a>
              <div className="flex items-start gap-4 text-white/70">
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                <span className="text-lg">Mlynské nivy 54<br />821 05 Bratislava</span>
              </div>
            </div>
            
            {/* Social */}
            <div className="flex gap-4 mt-8">
              {[Linkedin, Facebook, Instagram].map((Icon, i) => (
                <motion.a
                  key={i}
                  href="#"
                  whileHover={{ scale: 1.1, y: -2 }}
                  className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center hover:bg-[#E8A238] transition-colors"
                >
                  <Icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-lg mb-6">{t('footer.services', language)}</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/temporary" className="text-white/60 hover:text-[#E8A238] transition-colors">
                  {t('nav.temporaryEmployment', language)}
                </Link>
              </li>
              <li>
                <Link to="/recruitment" className="text-white/60 hover:text-[#E8A238] transition-colors">
                  {t('nav.recruitment', language)}
                </Link>
              </li>
              <li>
                <Link to="/consulting" className="text-white/60 hover:text-[#E8A238] transition-colors">
                  {t('nav.hrConsulting', language)}
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-bold text-lg mb-6">{t('footer.company', language)}</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/about" className="text-white/60 hover:text-[#E8A238] transition-colors">
                  {t('footer.aboutUs', language)}
                </Link>
              </li>
              <li>
                <button onClick={openContactModal} className="text-white/60 hover:text-[#E8A238] transition-colors">
                  {t('footer.contact', language)}
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-white/40 text-sm">
            {t('footer.copyright', language)}
          </p>
          <div className="flex gap-8">
            {[
              t('footer.privacy', language),
              t('footer.terms', language)
            ].map((item) => (
              <a key={item} href="#" className="text-white/40 hover:text-[#E8A238] transition-colors text-sm">
                {item}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
